// CleanupRealtyLinkOverlay — the picker that was missing for the
// realty-sr-needs-client-household issue (Nate 2026-07-09). The issue's
// "Pick the actual client household" button had no UI behind it, so it
// POSTed a param-less `match` and dead-ended on the backend's 400
// target_required. This overlay is that UI.
//
// Two paths, matching the backend's two branches
// (workers/api/src/routes/cleanup.ts realty `match` handler):
//   • Link to an EXISTING client household — search → pick → confirm →
//     decide(match, { target_household_id }). The synthetic "(Realty) …"
//     stub merges INTO the picked household (moves the SR + realty rows +
//     rhythms, soft-deletes the stub, ensures an active_client realty
//     relationship). Reversible via the destination's History-tab unmerge.
//     A would-create-duplicate block comes back as 409 would_duplicate;
//     we surface it with a "Link anyway" escape (allow_duplicate: true).
//   • Create a NEW realty-only client household — the buyer/seller isn't
//     in the database yet: rename the stub into a real household + attach
//     an optional buyer contact → decide(match, { create_client_household,
//     new_household_name, buyer_first_name?, buyer_last_name? }).
//
// The backend marks the issue resolved on success, so onLinked() just
// refreshes the host's queue.

import { useEffect, useState } from "react";

import { api, ApiError, type HouseholdListItem } from "../api";
import { Modal } from "../components/Modal";
import { Button } from "../ui";

type Mode = "existing" | "create";

export function CleanupRealtyLinkOverlay({
  issueId,
  serviceRecordLabel,
  decisionQuestion,
  onClose,
  onLinked,
}: {
  issueId: string;
  serviceRecordLabel: string;
  decisionQuestion: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [mode, setMode] = useState<Mode>("existing");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When the backend blocks the link as a would-be duplicate on the
  // target, we keep the message and offer "Link anyway" (allow_duplicate).
  const [duplicateBlock, setDuplicateBlock] = useState<string | null>(null);

  // --- existing-household search state ---
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [picked, setPicked] = useState<HouseholdListItem | null>(null);

  // --- create-household form state ---
  const [newName, setNewName] = useState("");
  const [buyerFirst, setBuyerFirst] = useState("");
  const [buyerLast, setBuyerLast] = useState("");

  // Debounced search (mirrors HouseholdMergePicker: 250ms, 2-char floor).
  useEffect(() => {
    if (mode !== "existing" || query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({ q: query.trim(), limit: 20 });
        if (cancelled) return;
        // A synthetic "(Realty) …" stub is never a valid destination.
        setResults(
          res.households.filter((h) => !h.display_name.startsWith("(Realty)")),
        );
        setSearchError(null);
      } catch (err) {
        if (cancelled) return;
        setSearchError(
          err instanceof ApiError ? err.message : "Search failed",
        );
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, mode]);

  async function linkToExisting(allowDuplicate: boolean) {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    if (!allowDuplicate) setDuplicateBlock(null);
    try {
      await api.decideCleanupIssue({
        issue_id: issueId,
        verb: "match",
        overrode_recommendation: false,
        params: {
          target_household_id: picked.id,
          ...(allowDuplicate ? { allow_duplicate: true } : {}),
        },
      });
      onLinked();
    } catch (err) {
      // A would-create-duplicate block comes back 409 with a message;
      // offer "Link anyway" rather than dead-ending.
      if (err instanceof ApiError && err.code === "would_duplicate") {
        setDuplicateBlock(
          err.message ||
            "This household already has a matching realty record. Link anyway to keep both.",
        );
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : "Couldn't link the record. Nothing was changed.",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function createClientHousehold() {
    if (busy) return;
    if (newName.trim().length === 0) {
      setError("Enter a name for the new client household.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.decideCleanupIssue({
        issue_id: issueId,
        verb: "match",
        overrode_recommendation: false,
        params: {
          create_client_household: true,
          new_household_name: newName.trim(),
          ...(buyerFirst.trim() ? { buyer_first_name: buyerFirst.trim() } : {}),
          ...(buyerLast.trim() ? { buyer_last_name: buyerLast.trim() } : {}),
        },
      });
      onLinked();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Couldn't create the household. Nothing was changed.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={true}
      title="Link this realty record to its client"
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {mode === "existing" ? (
            duplicateBlock ? (
              <Button onClick={() => linkToExisting(true)} disabled={busy}>
                {busy ? "Linking…" : "Link anyway"}
              </Button>
            ) : (
              <Button
                onClick={() => linkToExisting(false)}
                disabled={!picked || busy}
              >
                {busy
                  ? "Linking…"
                  : picked
                    ? `Link to "${picked.display_name}"`
                    : "Pick a household first"}
              </Button>
            )
          ) : (
            <Button
              onClick={createClientHousehold}
              disabled={busy || newName.trim().length === 0}
            >
              {busy ? "Creating…" : "Create household & link"}
            </Button>
          )}
        </>
      }
    >
      <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
        <strong>{decisionQuestion}</strong>
        <br />
        <span className="muted" style={{ fontSize: 12 }}>
          Record: {serviceRecordLabel}
        </span>
      </p>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => {
            setMode("existing");
            setError(null);
          }}
          style={modeTabStyle(mode === "existing")}
        >
          Link to an existing household
        </button>
        <button
          type="button"
          onClick={() => {
            setMode("create");
            setError(null);
            setDuplicateBlock(null);
          }}
          style={modeTabStyle(mode === "create")}
        >
          New realty-only client
        </button>
      </div>

      {error && (
        <p style={{ color: "var(--danger)", fontSize: 12.5, margin: "0 0 10px" }}>
          {error}
        </p>
      )}

      {mode === "existing" ? (
        <>
          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            Search for the client household — by the buyer's/seller's name or
            the property address
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setDuplicateBlock(null);
            }}
            placeholder="Type at least 2 characters…"
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-compact)",
              fontSize: 14,
            }}
            autoFocus
          />

          {searching && (
            <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
              Searching…
            </p>
          )}
          {searchError && (
            <p style={{ color: "var(--danger)", fontSize: 12, margin: "6px 0 0" }}>
              {searchError}
            </p>
          )}
          {!searching &&
            query.trim().length >= 2 &&
            results.length === 0 &&
            !searchError && (
              <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
                No matching households — try the property address, or create a
                new realty-only client above.
              </p>
            )}

          {results.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "8px 0 0",
                maxHeight: 240,
                overflowY: "auto",
                border: "1px solid var(--mid-gray)",
                borderRadius: "var(--radius-compact)",
              }}
            >
              {results.map((h) => {
                const isPicked = picked?.id === h.id;
                return (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setPicked(h);
                        setDuplicateBlock(null);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        background: isPicked
                          ? "rgba(74, 152, 198, 0.12)"
                          : "transparent",
                        border: "none",
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        borderBottom: "1px solid var(--mid-gray)",
                      }}
                    >
                      <div style={{ fontWeight: 600, color: "var(--navy)" }}>
                        {h.display_name}
                      </div>
                      {h.members_label && (
                        <div className="muted" style={{ fontSize: 11.5 }}>
                          {h.members_label}
                        </div>
                      )}
                      <div className="muted" style={{ fontSize: 11 }}>
                        {h.contact_count}{" "}
                        {h.contact_count === 1 ? "contact" : "contacts"}
                        {h.primary_city &&
                          h.primary_state &&
                          ` · ${h.primary_city}, ${h.primary_state}`}
                        {h.company_slugs.length > 0 &&
                          ` · ${h.company_slugs.join(", ")}`}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {duplicateBlock && (
            <div
              style={{
                border: "1px solid var(--blue, #4e98c6)",
                background: "rgba(74, 152, 198, 0.08)",
                borderRadius: "var(--radius-compact)",
                padding: "10px 12px",
                marginTop: 12,
                fontSize: 12.5,
                lineHeight: 1.45,
              }}
            >
              {duplicateBlock} Use <strong>Link anyway</strong> below to keep
              both records.
            </div>
          )}

          {picked && (
            <p style={{ fontSize: 12.5, marginTop: 12, color: "var(--fg-3)" }}>
              The synthetic "(Realty) …" household is dissolved into{" "}
              <strong>{picked.display_name}</strong> — the record and its
              property move there. Reversible afterward from{" "}
              <strong>{picked.display_name}</strong> → Actions → Undo a recent
              merge.
            </p>
          )}
        </>
      ) : (
        <>
          <p style={{ fontSize: 12.5, marginTop: 0, color: "var(--fg-3)" }}>
            Use this when the buyer/seller isn't in the database yet. The
            synthetic "(Realty) …" household is renamed into a real client
            household you name here; the record stays attached. Reversible via
            a 60-second undo.
          </p>
          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            New household name (required)
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Heffington Household"
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-compact)",
              fontSize: 14,
              marginBottom: 12,
            }}
            autoFocus
          />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label
                style={{ display: "block", fontSize: 13, marginBottom: 4 }}
              >
                Buyer first name (optional)
              </label>
              <input
                type="text"
                value={buyerFirst}
                onChange={(e) => setBuyerFirst(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--mid-gray)",
                  borderRadius: "var(--radius-compact)",
                  fontSize: 14,
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{ display: "block", fontSize: 13, marginBottom: 4 }}
              >
                Buyer last name (optional)
              </label>
              <input
                type="text"
                value={buyerLast}
                onChange={(e) => setBuyerLast(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--mid-gray)",
                  borderRadius: "var(--radius-compact)",
                  fontSize: 14,
                }}
              />
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

function modeTabStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    border: active ? "1px solid var(--blue)" : "1px solid var(--mid-gray)",
    background: active ? "var(--blue-tint, rgba(74,152,198,0.1))" : "var(--bg-0)",
    color: "var(--navy)",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
  };
}
